import { useQuery } from '@tanstack/react-query'
import { API_ENDPOINTS } from 'config/api'
import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { enrichTokenWithData, useTokenData } from 'contexts/TokenDataContext'
import { useConnection } from 'hooks/useConnection'
import { useMemo } from 'react'
import { Address } from 'viem'

import { apiClient } from '../../lib/api-client'

// Helper function to enrich a vault with token data
function enrichVaultWithTokenData(vault: any, tokenData: Record<Address, any>) {
  const enrichedVault = { ...vault }

  // Enrich token0 and token1
  if (vault.token0) {
    enrichedVault.token0 = enrichTokenWithData(vault.token0, tokenData)
  }
  if (vault.token1) {
    enrichedVault.token1 = enrichTokenWithData(vault.token1, tokenData)
  }

  // Enrich tokenLp
  if (vault.tokenLp) {
    enrichedVault.tokenLp = enrichTokenWithData(vault.tokenLp, tokenData)
  }

  // Enrich farm reward tokens
  if (vault.farm?.rewardTokens) {
    enrichedVault.farm = {
      ...vault.farm,
      rewardTokens: vault.farm.rewardTokens.map((token: any) => enrichTokenWithData(token, tokenData)),
    }
  }

  // Enrich baults tokens if they exist
  if (vault.baults) {
    enrichedVault.baults = vault.baults.map((bault: any) => {
      const enrichedBault = { ...bault }

      // Enrich any token fields in baults
      if (bault.token0) {
        enrichedBault.token0 = enrichTokenWithData(bault.token0, tokenData)
      }
      if (bault.token1) {
        enrichedBault.token1 = enrichTokenWithData(bault.token1, tokenData)
      }
      if (bault.tokenLp) {
        enrichedBault.tokenLp = enrichTokenWithData(bault.tokenLp, tokenData)
      }

      return enrichedBault
    })
  }

  return enrichedVault
}

// Master hook for all vaults data (includes islands, external vaults, and baults)
export function useMasterVaultsData(options?: { enabled?: boolean; userAddress?: Address }) {
  const { account: connectedAddress } = useConnection()
  const userAddress = options?.userAddress || connectedAddress
  const { tokenData } = useTokenData()

  const vaultQuery = useQuery({
    queryKey: ['masterVaults', userAddress],
    queryFn: async () => {
      const params: Record<string, any> = {
        orderBy: 'islandTvl',
        orderDirection: 'desc',
        limit: 1000, // Get all vaults
      }

      // Include user parameter if wallet is connected
      if (userAddress) {
        params.user = userAddress
      }

      const response = await apiClient.get<{ data: any[] }>(API_ENDPOINTS.vaults, params)
      return response.data
    },
    ...PORTFOLIO_REFETCH_POLICY,
    enabled: options?.enabled !== false,
  })

  // Enrich vault data with token information using useMemo
  const enrichedData = useMemo(() => {
    if (!vaultQuery.data || !tokenData) {
      return vaultQuery.data
    }

    return vaultQuery.data.map((vault: any) => enrichVaultWithTokenData(vault, tokenData))
  }, [vaultQuery.data, tokenData])

  return {
    ...vaultQuery,
    data: enrichedData,
  }
}

// Derived hook for islands (vaults with provider="kodiak")
export function useIslandsData(userAddress?: Address) {
  const { data: allVaults, ...rest } = useMasterVaultsData({ userAddress })

  const islands = allVaults?.filter((vault) => vault.provider === 'kodiak') || []

  return {
    data: islands,
    ...rest,
  }
}

// Derived hook for external vaults (vaults with provider!="kodiak")
export function useExternalVaultsData(userAddress?: Address) {
  const { data: allVaults, ...rest } = useMasterVaultsData({ userAddress })

  const externalVaults = allVaults?.filter((vault) => vault.provider !== 'kodiak') || []

  return {
    data: externalVaults,
    ...rest,
  }
}

// Derived hook for user islands (user has balance > 0)
export function useUserIslandsData() {
  const { account: userAddress, isConnected } = useConnection()
  const { data: allVaults, ...rest } = useMasterVaultsData({
    enabled: isConnected,
    userAddress,
  })

  const userIslands =
    allVaults?.filter(
      (vault) =>
        vault.provider === 'kodiak' && vault.vaultBalanceUSD && parseFloat(vault.vaultBalanceUSD.toString()) > 0
    ) || []

  return {
    data: userIslands,
    ...rest,
  }
}

// Derived hook for user external vaults (user has balance > 0)
export function useUserExternalVaultsData() {
  const { account: userAddress, isConnected } = useConnection()
  const { data: allVaults, ...rest } = useMasterVaultsData({
    enabled: isConnected,
    userAddress,
  })

  const userExternalVaults =
    allVaults?.filter(
      (vault) =>
        vault.provider !== 'kodiak' && vault.vaultBalanceUSD && parseFloat(vault.vaultBalanceUSD.toString()) > 0
    ) || []

  return {
    data: userExternalVaults,
    ...rest,
  }
}

// Derived hook for all baults across all vaults
export function useAllBaultsData(userAddress?: Address) {
  const { data: allVaults, ...rest } = useMasterVaultsData({ userAddress })

  const allBaults =
    allVaults?.flatMap((vault) =>
      (vault.baults || []).map((bault: any) => ({
        ...bault,
        vaultId: vault.id,
        vaultName: vault.tokenLp?.name || vault.id,
        vaultProvider: vault.provider,
      }))
    ) || []

  return {
    data: allBaults,
    ...rest,
  }
}

// Derived hook for user baults (user has balance > 0 in the vault)
export function useUserBaultsData() {
  const { account: userAddress, isConnected } = useConnection()
  const { data: allVaults, ...rest } = useMasterVaultsData({
    enabled: isConnected,
    userAddress,
  })

  const userBaults =
    allVaults?.flatMap((vault) => {
      if (!vault.baultBalanceUSD || parseFloat(vault.baultBalanceUSD.toString()) <= 0) {
        return []
      }

      return (vault.baults || []).map((bault: any) => ({
        ...bault,
        vaultId: vault.id,
        vaultName: vault.tokenLp?.name || vault.id,
        vaultProvider: vault.provider,
        userBalanceUSD: vault.baultBalanceUSD,
      }))
    }) || []

  return {
    data: userBaults,
    ...rest,
  }
}
